import { useQuery } from '@apollo/client';
import { setTemplateTranslations } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { hapticFeedback } from '@tma.js/sdk';
import { type FC, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    GetCategoriesQuery,
    GetLatestAchievementsQuery,
    GetLatestAchievementsQueryVariables,
    GetTemplatesQuery,
} from '../client/generated/graphql';
import { getLatestAchievements } from '../client/queries/achievement';
import { getCategoriesQuery } from '../client/queries/category';
import { getTemplatesQuery } from '../client/queries/template';
import { AchievementCategoriesView } from '../components/achievements/AchievementCategoriesView';
import { LatestAchievementsView } from '../components/achievements/LatestAchievementsView';
import { AchievementPageSkeleton } from '../components/achievements/skeleton/AchievementPageSkeleton';
import type { ListCategoryWithExternalDescription } from '../client/types';
import { addUnlinkedExternalTemplateCategories } from '../helpers/externalCategory';
import { openExternalLink } from '../helpers/externalLink';
import { floatingTabbarScrollBottomPadWithSatellite } from '../helpers/floatingTabbarLayout';
import { achievementCategoryPath, achievementViewPath } from '../navigation/paths';

export const AchievementPage: FC = () => {
    const navigate = useNavigate();

    const {
        loading: loadingCategories,
        error: errorCategories,
        data: categoriesData,
    } = useQuery<GetCategoriesQuery>(getCategoriesQuery, { fetchPolicy: 'cache-first' });
    const { data: templatesData } = useQuery<GetTemplatesQuery>(getTemplatesQuery, { fetchPolicy: 'cache-first' });
    if (templatesData) setTemplateTranslations(templatesData.templateCategories);
    const {
        loading: loadingAchievements,
        error: errorAchievements,
        data: achievementsData,
    } = useQuery<GetLatestAchievementsQuery, GetLatestAchievementsQueryVariables>(getLatestAchievements, {
        variables: { limit: 3, orderBy: 'achievedOn' },
        fetchPolicy: 'cache-first',
    });

    const categories = categoriesData?.categories ?? [];
    const latestAchievements = achievementsData?.achievements ?? [];

    const achievementCategories = useMemo<ListCategoryWithExternalDescription[]>(
        () => addUnlinkedExternalTemplateCategories(categories, templatesData?.templateCategories),
        [categories, templatesData],
    );

    if (loadingCategories || loadingAchievements) return <AchievementPageSkeleton />;
    if (errorCategories || errorAchievements) return <AchievementPageSkeleton />;
    if (!categoriesData || !achievementsData) return <AchievementPageSkeleton />;

    const handleCategoryClick = (category: ListCategoryWithExternalDescription) => {
        hapticFeedback.impactOccurred.ifAvailable('light');
        if (category.externalLink) {
            openExternalLink(category.externalLink);
            return;
        }
        const path = achievementCategoryPath.replace(':categoryId', category.id);
        navigate(path);
    };

    const handleAchievementClick = (achievementId: string) => {
        hapticFeedback.impactOccurred.ifAvailable('light');
        const path = achievementViewPath.replace(':id', achievementId);
        navigate(path);
    };

    return (
        <Stack sx={{ position: 'relative', height: '100%', overflow: 'hidden', bgcolor: 'var(--tg-theme-secondary-bg-color)' }}>
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    WebkitOverflowScrolling: 'touch',
                    paddingTop: 'calc(var(--tg-viewport-content-safe-area-inset-top, 0px) + var(--tg-viewport-safe-area-inset-top, 0px))',
                    paddingBottom: floatingTabbarScrollBottomPadWithSatellite,
                }}
            >
                <LatestAchievementsView achievements={latestAchievements} onAchievementClick={handleAchievementClick} />
                <AchievementCategoriesView categories={achievementCategories} onCategoryClick={handleCategoryClick} />
            </Box>
        </Stack>
    );
};
